import React, { useState, useEffect } from 'react';
import { X, Check, Users, MessageCircle, AlertTriangle } from 'lucide-react';
import Button from '../UI/Button';

interface Guest {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email?: string;
  status: string;
}

interface RescheduleConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedGuestIds: string[]) => void;
  guests: Guest[];
  eventTitle: string;
  newDate?: string;
  newTime?: string;
  loading?: boolean;
  theme: 'dark' | 'light';
}

export const RescheduleConfirmationDialog: React.FC<RescheduleConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  guests,
  eventTitle,
  newDate,
  newTime,
  loading = false,
  theme
}) => {
  const [selectedGuests, setSelectedGuests] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Reset selection when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedGuests(new Set());
      setSelectAll(false);
    }
  }, [isOpen]);

  // Handle select all toggle
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedGuests(new Set());
      setSelectAll(false);
    } else {
      const allGuestIds = new Set(guests.map(guest => guest.id));
      setSelectedGuests(allGuestIds);
      setSelectAll(true);
    }
  };

  // Handle individual guest selection
  const handleGuestSelect = (guestId: string) => {
    const newSelection = new Set(selectedGuests);
    if (newSelection.has(guestId)) {
      newSelection.delete(guestId);
    } else {
      newSelection.add(guestId);
    }
    setSelectedGuests(newSelection);
    setSelectAll(newSelection.size === guests.length);
  };

  // Handle confirmation
  const handleConfirm = () => {
    onConfirm(Array.from(selectedGuests));
  };

  // Format status for display
  const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      'pending': 'Pending',
      'accepted': 'Confirmed',
      'rejected': 'Declined',
      'ignored': 'No Response'
    };
    return statusMap[status] || status;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'pending': 'text-yellow-600',
      'accepted': 'text-green-600',
      'rejected': 'text-red-600',
      'ignored': 'text-gray-600'
    };
    return colorMap[status] || 'text-gray-600';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-4xl max-h-[90vh] rounded-xl shadow-xl ${
        theme === 'dark' 
          ? 'bg-gray-800 border border-gray-700' 
          : 'bg-white border border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${
              theme === 'dark' ? 'bg-orange-900/30' : 'bg-orange-100'
            }`}>
              <AlertTriangle className={`h-5 w-5 ${
                theme === 'dark' ? 'text-orange-400' : 'text-orange-600'
              }`} />
            </div>
            <div>
              <h2 className={`text-xl font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Event Rescheduled
              </h2>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Select guests to notify about the schedule change
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Event Info */}
        <div className={`p-6 border-b ${
          theme === 'dark' ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className={`font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Event: {eventTitle}
              </h3>
            </div>
            {(newDate || newTime) && (
              <div>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  New Schedule: {newDate} {newTime}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Guest Selection */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-medium ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Select Guests to Notify
            </h3>
            <div className="flex items-center space-x-2">
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {selectedGuests.size} of {guests.length} selected
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSelectAll}
                className="flex items-center"
              >
                <Check className="h-4 w-4 mr-1" />
                {selectAll ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>

          {/* Guest Table */}
          <div className={`border rounded-lg overflow-hidden ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className={`${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                }`}>
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className={`rounded ${
                          theme === 'dark' 
                            ? 'border-gray-600 bg-gray-800 text-blue-500' 
                            : 'border-gray-300 bg-white text-blue-600'
                        }`}
                      />
                    </th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Name
                    </th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Phone
                    </th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${
                  theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
                }`}>
                  {guests.map((guest) => (
                    <tr
                      key={guest.id}
                      className={`hover:${
                        theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                      } transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedGuests.has(guest.id)}
                          onChange={() => handleGuestSelect(guest.id)}
                          className={`rounded ${
                            theme === 'dark' 
                              ? 'border-gray-600 bg-gray-800 text-blue-500' 
                              : 'border-gray-300 bg-white text-blue-600'
                          }`}
                        />
                      </td>
                      <td className={`px-4 py-3 text-sm ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {guest.first_name} {guest.last_name}
                      </td>
                      <td className={`px-4 py-3 text-sm ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {guest.phone}
                      </td>
                      <td className={`px-4 py-3 text-sm ${getStatusColor(guest.status)}`}>
                        {formatStatus(guest.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {guests.length === 0 && (
            <div className={`text-center py-8 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No guests found for this event</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between p-6 border-t ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center space-x-2">
            <MessageCircle className={`h-4 w-4 ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
            }`} />
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              WhatsApp notifications will be sent to selected guests
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={loading || selectedGuests.size === 0}
              className="flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Send Notifications ({selectedGuests.size})
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
